"use client";

import { c as _c } from "react/compiler-runtime";
import { motion, useReducedMotion } from "framer-motion";
import { Video, ImageIcon, Play, Check, Clock, Eye, Heart, Shield, Smartphone, Lock, Wand2, TrendingUp } from "lucide-react";

// Animation constants - premium easing curves
const EASE_OUT = [0.22, 1, 0.36, 1] as const;
const SPRING = {
  type: "spring",
  stiffness: 400,
  damping: 30
} as const;
const SPRING_HOVER = {
  type: "spring",
  stiffness: 400,
  damping: 35
} as const;

// Flag URL helper - uses same CDN as FollowersMapChart
const getFlagUrl = (countryCode: string) => `https://hatscripts.github.io/circle-flags/flags/${countryCode.toLowerCase()}.svg`;

// Social platform icons with brand colors
const SocialIcon = t0 => {
  const $ = _c(14);
  const {
    platform
  } = t0;
  const shouldReduceMotion = useReducedMotion();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      bg: "bg-gray-900",
      icon: <svg className="size-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      bg: "bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400",
      icon: <svg className="size-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      bg: "bg-red-600",
      icon: <svg className="size-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      bg: "bg-red-700",
      icon: <svg className="size-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" /></svg>
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      bg: "bg-blue-700",
      icon: <svg className="size-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      tiktok: t1,
      instagram: t2,
      youtube: t3,
      pinterest: t4,
      linkedin: t5,
      facebook: {
        bg: "bg-blue-600",
        icon: <svg className="size-5 text-white" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
      }
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const icons = t6;
  const data = icons[platform];
  const t7 = `flex size-10 items-center justify-center rounded-xl shadow-sm ${data.bg}`;
  let t8;
  if ($[6] !== shouldReduceMotion) {
    t8 = shouldReduceMotion ? undefined : {
      opacity: 0.9
    };
    $[6] = shouldReduceMotion;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      duration: 0.15,
      ease: "easeOut"
    };
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const t10 = `${platform} social media`;
  let t11;
  if ($[9] !== data.icon || $[10] !== t10 || $[11] !== t7 || $[12] !== t8) {
    t11 = <motion.div className={t7} whileHover={t8} transition={t9} role="img" aria-label={t10}>{data.icon}</motion.div>;
    $[9] = data.icon;
    $[10] = t10;
    $[11] = t7;
    $[12] = t8;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  return t11;
};
const Tag = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Check className="size-4 text-emerald-500" aria-hidden="true" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="flex items-center gap-1.5">{t1}<span className="text-[13px] font-medium text-gray-600">{children}</span></div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};

// Subtle indicator for premium badge replacement - Linear/Stripe style
const SubtleIndicator = t0 => {
  const $ = _c(7);
  const {
    children,
    icon: Icon
  } = t0;
  let t1;
  if ($[0] !== Icon) {
    t1 = Icon && <Icon className="size-3.5 text-gray-400" aria-hidden="true" />;
    $[0] = Icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span>{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="flex items-center gap-1.5 text-[12px] font-medium text-gray-500">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

// Card 1: Content Creation Studio - polished, professional look
const ContentCreationVisual = () => {
  const $ = _c(24);
  const shouldReduceMotion = useReducedMotion();
  let t0;
  if ($[0] !== shouldReduceMotion) {
    t0 = shouldReduceMotion ? {} : {
      y: [0, -3, 0]
    };
    $[0] = shouldReduceMotion;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      scale: 1.02
    };
    t2 = {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut"
    };
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      scale: 1.03
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0 flex items-center justify-center"><motion.button className="relative flex size-8 items-center justify-center rounded-full bg-white/95 shadow-sm before:absolute before:inset-[-8px] before:content-[''] touch-manipulation" whileHover={t3} transition={SPRING_HOVER} aria-label="Play video preview"><Play className="size-4 text-violet-600 ml-0.5" fill="currentColor" aria-hidden="true" /></motion.button></div>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t0) {
    t5 = <motion.div className="relative size-16 overflow-hidden rounded-xl bg-gradient-to-br from-violet-100 to-fuchsia-100 shadow-sm" animate={t0} whileHover={t1} transition={t2}>{t4}</motion.div>;
    $[6] = t0;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = [{
      gradient: "from-rose-200 to-rose-300",
      icon: ImageIcon
    }, {
      gradient: "from-sky-200 to-sky-300",
      icon: Video
    }];
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== shouldReduceMotion) {
    t7 = <div className="flex flex-col gap-2">{t6.map((item, i) => <motion.div key={i} className={`size-[30px] overflow-hidden rounded-lg bg-gradient-to-br ${item.gradient} shadow-md`} initial={shouldReduceMotion ? {} : {
        opacity: 0,
        scale: 0.8
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        delay: 0.1 + i * 0.04,
        ease: EASE_OUT
      }}><div className="flex h-full items-center justify-center"><item.icon className="size-3.5 text-white" aria-hidden="true" /></div></motion.div>)}</div>;
    $[9] = shouldReduceMotion;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <div className="relative flex items-center gap-3">{t5}{t7}</div>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== shouldReduceMotion) {
    t9 = shouldReduceMotion ? {} : {
      opacity: 0
    };
    $[14] = shouldReduceMotion;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  let t11;
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      opacity: 1
    };
    t11 = {
      duration: 0.35,
      delay: 0.25,
      ease: EASE_OUT
    };
    t12 = <SubtleIndicator icon={Wand2}>Powered by AI</SubtleIndicator>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t10 = $[16];
    t11 = $[17];
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t9) {
    t13 = <motion.div className="mt-4" initial={t9} animate={t10} transition={t11}>{t12}</motion.div>;
    $[19] = t9;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t13 || $[22] !== t8) {
    t14 = <div className="relative flex h-full flex-col items-center justify-center">{t8}{t13}</div>;
    $[21] = t13;
    $[22] = t8;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  return t14;
};

// Card 2: Unified Social Manager
const SocialManagerVisual = () => {
  const $ = _c(13);
  const shouldReduceMotion = useReducedMotion();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["tiktok", "instagram", "youtube", "pinterest", "linkedin", "facebook"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const platforms = t0;
  let t1;
  if ($[1] !== shouldReduceMotion) {
    t1 = <div className="grid grid-cols-3 gap-2.5">{platforms.map((platform, i) => <motion.div key={platform} initial={shouldReduceMotion ? {} : {
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        ...SPRING,
        delay: i * 0.05
      }}><SocialIcon platform={platform} /></motion.div>)}</div>;
    $[1] = shouldReduceMotion;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== shouldReduceMotion) {
    t2 = shouldReduceMotion ? {} : {
      opacity: 0
    };
    $[3] = shouldReduceMotion;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      opacity: 1
    };
    t4 = {
      duration: 0.3,
      delay: 0.35,
      ease: EASE_OUT
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center gap-1.5 text-[12px] font-medium text-gray-500"><span className="size-1.5 rounded-full bg-emerald-500" aria-hidden="true" /><span>All 6 connected</span></div>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2) {
    t6 = <motion.div initial={t2} animate={t3} transition={t4}>{t5}</motion.div>;
    $[8] = t2;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t6) {
    t7 = <div className="relative flex h-full flex-col items-center justify-center gap-4">{t1}{t6}</div>;
    $[10] = t1;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};

// Card 3: Deep Analytics
const AnalyticsVisual = () => {
  const $ = _c(9);
  const shouldReduceMotion = useReducedMotion();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [35, 55, 40, 70, 50, 85, 65, 95, 80];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const barHeights = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      icon: Eye,
      label: "Views",
      value: "1.2M",
      growth: "128%"
    }, {
      icon: Heart,
      label: "Likes",
      value: "84K",
      growth: "45%"
    }];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== shouldReduceMotion) {
    t2 = <div className="flex gap-3">{t1.map((stat, i) => <motion.div key={stat.label} className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 shadow-sm" initial={shouldReduceMotion ? {} : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        delay: i * 0.06,
        ease: EASE_OUT
      }}><div className="flex items-center gap-2"><stat.icon className="size-4 text-gray-400" aria-hidden="true" /><span className="text-[12px] font-semibold text-gray-500">{stat.label}</span></div><div className="mt-1 flex items-baseline gap-2"><span className="text-xl font-bold tabular-nums tracking-tight text-gray-900">{stat.value}</span><span className="flex items-center gap-0.5 text-[12px] font-semibold text-emerald-600"><TrendingUp className="size-3" aria-hidden="true" />{stat.growth}</span></div></motion.div>)}</div>;
    $[2] = shouldReduceMotion;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== shouldReduceMotion) {
    t3 = <div className="flex h-10 items-end gap-1.5 rounded-lg bg-white/60 px-3 py-2" role="img" aria-label="Growth trend chart">{barHeights.map((h, i_0) => <motion.div key={i_0} className="w-4 rounded-t bg-blue-500 origin-bottom" aria-hidden="true" initial={shouldReduceMotion ? {} : {
        scaleY: 0
      }} animate={{
        scaleY: 1
      }} transition={{
        duration: 0.3,
        delay: 0.15 + i_0 * 0.03,
        ease: EASE_OUT
      }} style={{
        height: `${h}%`
      }} />)}</div>;
    $[4] = shouldReduceMotion;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex h-full flex-col items-center justify-center gap-3">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
};

// Card 4: Viral Automations
const AutomationsVisual = () => {
  const $ = _c(14);
  const shouldReduceMotion = useReducedMotion();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["Mon", "Tue", "Wed", "Thu", "Fri"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const days = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["done", "done", "done", "pending", "pending"];
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const statuses = t1;
  let t2;
  if ($[2] !== shouldReduceMotion) {
    t2 = <div className="flex gap-2" role="list" aria-label="Weekly schedule">{days.map((day, i) => <motion.div key={i} className={`flex size-12 flex-col items-center justify-center rounded-xl border shadow-sm ${statuses[i] === "done" ? "border-emerald-200 bg-emerald-50" : "border-gray-200 bg-white"}`} initial={shouldReduceMotion ? {} : {
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.25,
        delay: i * 0.04,
        ease: EASE_OUT
      }} role="listitem" aria-label={`${day}: ${statuses[i] === "done" ? "completed" : "pending"}`}><span className="text-[11px] font-semibold text-gray-500">{day}</span>{statuses[i] === "done" ? <Check className="size-5 text-emerald-500" strokeWidth={2.5} aria-hidden="true" /> : <Clock className="size-5 text-gray-300" aria-hidden="true" />}</motion.div>)}</div>;
    $[2] = shouldReduceMotion;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex items-center justify-between text-[12px] mb-1.5"><span className="font-semibold text-gray-600">Campaign Progress</span><span className="tabular-nums font-bold text-emerald-600">60%</span></div>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== shouldReduceMotion) {
    t4 = shouldReduceMotion ? {
      scaleX: 0.6
    } : {
      scaleX: 0
    };
    $[5] = shouldReduceMotion;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      scaleX: 0.6
    };
    t6 = {
      duration: 0.35,
      delay: 0.25,
      ease: EASE_OUT
    };
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4) {
    t7 = <div className="w-full max-w-[240px]">{t3}<div className="h-2.5 overflow-hidden rounded-full bg-gray-200" role="progressbar" aria-valuenow={60} aria-valuemin={0} aria-valuemax={100} aria-label="Campaign progress"><motion.div className="h-full w-full origin-left rounded-full bg-emerald-500" initial={t4} animate={t5} transition={t6} /></div></div>;
    $[9] = t4;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t2 || $[12] !== t7) {
    t8 = <div className="flex h-full flex-col items-center justify-center gap-4">{t2}{t7}</div>;
    $[11] = t2;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};

// Card 5: Team Workspaces
const TeamVisual = () => {
  const $ = _c(25);
  const shouldReduceMotion = useReducedMotion();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [{
      bg: "bg-blue-500",
      initials: "JD"
    }, {
      bg: "bg-purple-500",
      initials: "AK"
    }, {
      bg: "bg-pink-500",
      initials: "ML"
    }, {
      bg: "bg-emerald-500",
      initials: "SR"
    }];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const avatars = t0;
  let t1;
  if ($[1] !== shouldReduceMotion) {
    t1 = avatars.map((avatar, i) => {
      const offset = (i - 1.5) * 12;
      return <motion.div key={i} className={`relative flex size-12 items-center justify-center rounded-full border-2 border-white ${avatar.bg} shadow-sm`} style={{
        zIndex: avatars.length - i
      }} initial={shouldReduceMotion ? {} : {
        opacity: 0,
        x: -offset
      }} animate={{
        opacity: 1,
        x: 0
      }} whileHover={{
        scale: 1.02
      }} transition={{
        ...SPRING,
        delay: i * 0.06
      }} role="img" aria-label={`Team member ${avatar.initials}`}><span className="text-[13px] font-bold text-white">{avatar.initials}</span></motion.div>;
    });
    $[1] = shouldReduceMotion;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      zIndex: 0
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== shouldReduceMotion) {
    t3 = shouldReduceMotion ? {} : {
      opacity: 0
    };
    $[4] = shouldReduceMotion;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      opacity: 1
    };
    t5 = {
      scale: 1.02
    };
    t6 = {
      duration: 0.3,
      delay: 0.3,
      ease: EASE_OUT
    };
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-[13px] font-semibold text-gray-400">+3</span>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t3) {
    t8 = <motion.div className="relative flex size-12 items-center justify-center rounded-full border-2 border-dashed border-gray-300 bg-gray-50" style={t2} initial={t3} animate={t4} whileHover={t5} transition={t6} role="img" aria-label="3 additional team members">{t7}</motion.div>;
    $[10] = t3;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t1 || $[13] !== t8) {
    t9 = <div className="flex items-center"><div className="isolate flex -space-x-3">{t1}{t8}</div></div>;
    $[12] = t1;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== shouldReduceMotion) {
    t10 = shouldReduceMotion ? {} : {
      opacity: 0
    };
    $[15] = shouldReduceMotion;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  let t12;
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 1
    };
    t12 = {
      duration: 0.3,
      delay: 0.4,
      ease: EASE_OUT
    };
    t13 = <span className="text-[12px] font-medium text-gray-500">7 collaborators</span>;
    $[17] = t11;
    $[18] = t12;
    $[19] = t13;
  } else {
    t11 = $[17];
    t12 = $[18];
    t13 = $[19];
  }
  let t14;
  if ($[20] !== t10) {
    t14 = <motion.div initial={t10} animate={t11} transition={t12}>{t13}</motion.div>;
    $[20] = t10;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t14 || $[23] !== t9) {
    t15 = <div className="flex h-full flex-col items-center justify-center gap-4">{t9}{t14}</div>;
    $[22] = t14;
    $[23] = t9;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  return t15;
};

// Card 6: Safe & Secure - with real flag icons and device variation
const SecurityVisual = () => {
  const $ = _c(24);
  const shouldReduceMotion = useReducedMotion();
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== shouldReduceMotion) {
    const devices = [{
      country: "us",
      label: "US",
      type: "iPhone"
    }, {
      country: "gb",
      label: "UK",
      type: "Pixel"
    }, {
      country: "ca",
      label: "CA",
      type: "Galaxy"
    }];
    t2 = "relative flex h-full flex-col items-center justify-center gap-4 overflow-hidden";
    const t4 = shouldReduceMotion ? {} : {
      opacity: [0.6, 0.8, 0.6]
    };
    let t5;
    let t6;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      };
      t6 = <Shield className="size-36 text-emerald-100/80" strokeWidth={1} aria-hidden="true" />;
      $[5] = t5;
      $[6] = t6;
    } else {
      t5 = $[5];
      t6 = $[6];
    }
    if ($[7] !== t4) {
      t3 = <motion.div className="absolute -right-8 -top-8 pointer-events-none select-none" animate={t4} transition={t5}>{t6}</motion.div>;
      $[7] = t4;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    t0 = "relative z-10 flex gap-3";
    t1 = devices.map((device, i) => <motion.div key={i} className="flex flex-col items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-3 shadow-sm" initial={shouldReduceMotion ? {} : {
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} whileHover={{
      scale: 1.01
    }} transition={{
      duration: 0.3,
      delay: i * 0.05,
      ease: EASE_OUT
    }}><div className="flex size-11 items-center justify-center rounded-lg bg-gray-100"><Smartphone className="size-6 text-gray-600" aria-hidden="true" /></div><div className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1"><img src={getFlagUrl(device.country)} alt={`${device.label} flag`} className="size-4 rounded-full ring-1 ring-gray-900/10" /><Lock className="size-3 text-emerald-600" aria-hidden="true" /></div><span className="text-[11px] font-semibold text-gray-500">{device.type}</span></motion.div>);
    $[0] = shouldReduceMotion;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[9] !== t0 || $[10] !== t1) {
    t4 = <div className={t0}>{t1}</div>;
    $[9] = t0;
    $[10] = t1;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== shouldReduceMotion) {
    t5 = shouldReduceMotion ? {} : {
      opacity: 0
    };
    $[12] = shouldReduceMotion;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  let t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      opacity: 1
    };
    t7 = {
      duration: 0.3,
      delay: 0.25,
      ease: EASE_OUT
    };
    t8 = <SubtleIndicator icon={Lock}>End-to-end encrypted</SubtleIndicator>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t5) {
    t9 = <motion.div initial={t5} animate={t6} transition={t7}>{t8}</motion.div>;
    $[17] = t5;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t9) {
    t10 = <div className={t2}>{t3}{t4}{t9}</div>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
};
interface FeatureCard {
  title: string;
  description: string;
  tags: string[];
  Visual: () => React.ReactNode;
}

// Static feature data hoisted outside component to prevent re-creation on every render
const FEATURES: FeatureCard[] = [{
  title: "Content creation studio",
  description: "Generate 4K videos and images with Sora 2 and Veo 3",
  tags: ["Top AI models", "Trending slideshows"],
  Visual: ContentCreationVisual
}, {
  title: "All-in-one social manager",
  description: "Auto-post to TikTok, Instagram, YouTube, Pinterest, and more",
  tags: ["Cross-platform", "Unlimited accounts"],
  Visual: SocialManagerVisual
}, {
  title: "Deep analytics",
  description: "Track competitors, campaigns, and growth in one dashboard",
  tags: ["Competitor tracking", "Real-time stats"],
  Visual: AnalyticsVisual
}, {
  title: "Smart automations",
  description: "Turn images into trending slideshows with smart scheduling",
  tags: ["Auto-generated", "Smart hooks"],
  Visual: AutomationsVisual
}, {
  title: "Team workspaces",
  description: "Collaborate with VAs and editors without sharing passwords",
  tags: ["Shared library", "Role-based access"],
  Visual: TeamVisual
}, {
  title: "Safe & secure",
  description: "Launch on pre-warmed US mobile devices with end-to-end encryption",
  tags: ["Dedicated devices", "Instant reach"],
  Visual: SecurityVisual
}];
export default function BentoFeaturesGrid() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3" role="list" aria-label="Product features">{FEATURES.map(_temp2)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp2(feature, index) {
  return <article key={index} role="listitem" className="group rounded-2xl border border-gray-200 bg-white p-5 transition-colors duration-200 hover:border-gray-300"><div className="h-48 rounded-xl border border-gray-100 bg-gray-50/80"><feature.Visual /></div><div className="mt-4"><h3 className="text-[15px] font-semibold leading-tight text-gray-900 text-balance">{feature.title}</h3><p className="mt-1.5 text-pretty text-[13px] leading-relaxed text-gray-600">{feature.description}</p><div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 border-t border-gray-100 pt-3">{feature.tags.map(_temp)}</div></div></article>;
}
function _temp(tag, i) {
  return <Tag key={i}>{tag}</Tag>;
}